import { setUploadProgress } from '@/store/slices/poc/heliaSlice';
import type { AppListenerEffectAPI } from '@/store/listeners/types';
import { announceFileKeyReadyThunk } from '@/store/thunks/poc/messaging/announceFileKeyReadyThunk';
import { sendFileAnnouncement } from '@/store/thunks/poc/messaging/announceFileReadyThunk';

export const MAX_RESEND_ATTEMPTS = 3;

const ACK_TIMEOUT_MS = 5000;
const activePendingAckTasks = new Map<string, ReturnType<AppListenerEffectAPI['fork']>>();

function stopPendingAckTask(messageId: string): void {
  const task = activePendingAckTasks.get(messageId);
  if (task !== undefined) {
    task.cancel();
    activePendingAckTasks.delete(messageId);
  }
}

export function startFileAnnouncementAckTimer(
  listenerApi: AppListenerEffectAPI,
  args: {
    messageId: string;
    sixId: string;
    cid: string;
    uploadId: string;
    metadata: Parameters<typeof sendFileAnnouncement>[0]['metadata'];
    targetPeerId?: string;
    attemptCount: number;
  }
): void {
  stopPendingAckTask(args.messageId);

  const task = listenerApi.fork(async(forkApi) => {
    try {
      await forkApi.delay(ACK_TIMEOUT_MS);

      if (args.attemptCount >= MAX_RESEND_ATTEMPTS) {
        listenerApi.dispatch(setUploadProgress({
          uploadId: args.uploadId,
          fileName: args.metadata.name,
          cid: args.cid,
          sixId: args.sixId,
          metadata: args.metadata,
          stage: 'error',
          progress: 0,
          error: 'Upload announcement failed after multiple attempts.',
        }));
        return;
      }

      void listenerApi.dispatch(sendFileAnnouncement({
        sixId: args.sixId,
        cid: args.cid,
        uploadId: args.uploadId,
        metadata: args.metadata,
        targetPeerId: args.targetPeerId,
        attemptCount: args.attemptCount + 1,
      }));
    } finally {
      if (activePendingAckTasks.get(args.messageId) === task) {
        activePendingAckTasks.delete(args.messageId);
      }
    }
  });

  activePendingAckTasks.set(args.messageId, task);
}

export function startFileKeyAckTimer(
  listenerApi: AppListenerEffectAPI,
  args: {
    messageId: string;
    sixId: string;
    fileId: string;
    newFileKeyCID: string;
    newFileKeyForUserId: string;
    newFileKeyCreatorUserId: string;
    metadata: Parameters<typeof announceFileKeyReadyThunk>[0]['metadata'];
    attemptCount: number;
  }
): void {
  stopPendingAckTask(args.messageId);

  const task = listenerApi.fork(async(forkApi) => {
    try {
      await forkApi.delay(ACK_TIMEOUT_MS);

      if (args.attemptCount >= MAX_RESEND_ATTEMPTS) {
        return;
      }

      void listenerApi.dispatch(announceFileKeyReadyThunk({
        sixId: args.sixId,
        fileId: args.fileId,
        newFileKeyCID: args.newFileKeyCID,
        newFileKeyForUserId: args.newFileKeyForUserId,
        newFileKeyCreatorUserId: args.newFileKeyCreatorUserId,
        metadata: args.metadata,
        attemptCount: args.attemptCount + 1,
      }));
    } finally {
      if (activePendingAckTasks.get(args.messageId) === task) {
        activePendingAckTasks.delete(args.messageId);
      }
    }
  });

  activePendingAckTasks.set(args.messageId, task);
}

export function cancelPendingAckTask(messageId: string): void {
  stopPendingAckTask(messageId);
}

export function cancelAllPendingAckTasks(): void {
  activePendingAckTasks.forEach((task) => {
    task.cancel();
  });
  activePendingAckTasks.clear();
}
